import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { PaperPlaneRight, Robot, User, FilePdf, ChatCircleText, Phone } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function GuestChatWidget() {
  const { propertyId } = useParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [started, setStarted] = useState(false);
  const [propertyName, setPropertyName] = useState('');
  const [smsNumber, setSmsNumber] = useState('');
  const messagesEnd = useRef(null);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch property name + SMS concierge number
  useEffect(() => {
    axios.get(`${API}/property-name/${propertyId}`)
      .then(r => { setPropertyName(r.data.name || ''); setSmsNumber(r.data.sms_number || ''); })
      .catch(() => { setPropertyName(''); setSmsNumber(''); });
  }, [propertyId]);

  const startChat = (e) => {
    e.preventDefault();
    if (!guestName.trim()) return;
    setStarted(true);
    const name = propertyName || 'your property';
    const smsHint = smsNumber ? ` You can also text or call me anytime at ${smsNumber} — whichever is easier for you.` : '';
    setMessages([{ role: 'assistant', content: `Hi ${guestName}! Welcome to ${name}. I'm your AI concierge — here to help you during your stay. Ask me about check-in, WiFi, local recommendations, or anything else.${smsHint}` }]);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat/${propertyId}`, {
        message: msg,
        guest_name: guestName,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I'm having trouble right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4" data-testid="guest-chat-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 flex flex-col" style={{ height: '600px' }}>
        {/* Header */}
        <div className="px-4 py-3 bg-[#0052FF] text-white flex items-center justify-between" data-testid="chat-header">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Robot weight="bold" size={16} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{propertyName || 'Property Concierge'}</p>
              <p className="text-xs text-white/70">AI-powered guest concierge</p>
            </div>
          </div>
          <button onClick={async () => {
            const res = await fetch(`${API}/properties/${propertyId}/welcome-guide`);
            const blob = await res.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'Welcome-Guide.pdf';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/20 hover:bg-white/30 rounded-sm text-xs font-bold transition-colors" data-testid="chat-pdf-btn">
            <FilePdf size={14} /> Welcome Guide
          </button>
        </div>

        {smsNumber && (
          <div className="px-4 py-2.5 bg-gradient-to-r from-blue-50 to-white border-b border-zinc-200 flex items-center gap-2" data-testid="sms-concierge-banner">
            <div className="w-7 h-7 bg-[#0052FF] rounded-sm flex items-center justify-center shrink-0">
              <ChatCircleText size={14} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-zinc-900">Prefer to text? Same AI concierge, on your phone.</p>
              <a href={`sms:${smsNumber}?body=${encodeURIComponent('Hi')}`} className="text-xs text-[#0052FF] font-bold hover:underline" data-testid="sms-concierge-link">
                Text or call {smsNumber} — tap to say Hi
              </a>
            </div>
            <a href={`tel:${smsNumber}`} title="Call" className="p-1.5 hover:bg-zinc-100 rounded-sm shrink-0" data-testid="sms-concierge-call">
              <Phone size={14} weight="duotone" className="text-[#0052FF]" />
            </a>
          </div>
        )}

        {!started ? (
          /* Name Entry */
          <div className="flex-1 flex items-center justify-center p-6">
            <form onSubmit={startChat} className="w-full text-center">
              <Robot size={40} weight="duotone" className="text-[#0052FF] mx-auto mb-4" />
              <h2 className="text-lg font-bold text-zinc-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {propertyName ? `Welcome to ${propertyName}!` : 'Welcome!'}
              </h2>
              <p className="text-sm text-zinc-500 mb-6">Enter your name to chat with {propertyName ? `the ${propertyName}` : 'our'} AI concierge.</p>
              <input
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
                className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm mb-4"
                placeholder="Your name"
                autoFocus
                data-testid="guest-name-input"
              />
              <button type="submit" className="w-full py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD]" data-testid="start-chat-btn">
                Start Chat
              </button>
            </form>
          </div>
        ) : (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${i}`}>
                  <div className={`flex items-start gap-2 max-w-[85%] ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === 'user' ? 'bg-zinc-200' : 'bg-[#0052FF]'}`}>
                      {m.role === 'user' ? <User size={12} className="text-zinc-600" /> : <Robot size={12} className="text-white" />}
                    </div>
                    <div className={`px-3 py-2 text-sm rounded-sm ${m.role === 'user' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-800'}`}>
                      {m.content}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start" data-testid="chat-typing">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-[#0052FF] rounded-full flex items-center justify-center">
                      <Robot size={12} className="text-white" />
                    </div>
                    <div className="px-3 py-2 bg-zinc-100 rounded-sm text-sm text-zinc-500">Typing...</div>
                  </div>
                </div>
              )}
              <div ref={messagesEnd} />
            </div>

            {/* Input */}
            <form onSubmit={send} className="p-3 border-t border-zinc-200 flex gap-2" data-testid="chat-input-form">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                className="flex-1 px-3 py-2 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm"
                placeholder="Ask anything about your stay..."
                disabled={loading}
                data-testid="chat-message-input"
              />
              <button type="submit" disabled={loading || !input.trim()} className="px-3 py-2 bg-[#0052FF] text-white rounded-sm hover:bg-[#003EDD] disabled:opacity-50" data-testid="chat-send-btn">
                <PaperPlaneRight weight="bold" size={16} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
